import { Icon } from './Icon.js';

/**
 * An ordered choice from a known set, where the ORDER is the meaning.
 *
 * Carriers in failover order, codecs in preference order: in both, the first
 * entry is what happens and the rest are what happens next, so the position has
 * to be visible and correctable. Explicit earlier/later controls rather than
 * tick-order, because tick-order cannot fix a mistake without unticking
 * everything chosen after it.
 *
 * One component for every such list, so "first, then the rest in order" cannot
 * come to mean different things on different screens.
 */

export interface PickerItem {
  id: string;
  label: string;
  /** Secondary text on the same line: a carrier and format, a codec's use. */
  detail?: string;
}

export function OrderedPicker({
  items, value, onChange, disabled = false, laterLabel, emptyText = 'None chosen.', mono = false,
}: {
  items: PickerItem[];
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** Said beside every entry after the first: "failover", "fallback". */
  laterLabel?: string;
  emptyText?: string;
  /** Monospace labels, for identifiers such as codec names. */
  mono?: boolean;
}) {
  const find = (id: string) => items.find((i) => i.id === id);
  const move = (i: number, by: number) => {
    const j = i + by;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  const available = items.filter((i) => !value.includes(i.id));

  return (
    <div className="arc-stack" style={{ gap: 6 }}>
      {value.map((id, i) => {
        const item = find(id);
        const name = item?.label ?? id.slice(0, 8);
        return (
          <div key={id} className="arc-row" style={{
            gap: 8, padding: '5px 9px', border: '1px solid var(--arc-border)',
            borderRadius: 'var(--arc-r-sm)',
          }}>
            <span className="arc-key-chip" style={{ width: 22, height: 22, fontSize: 11 }}>{i + 1}</span>
            <span className={mono ? 'arc-mono' : undefined} style={{ fontWeight: 550 }}>{name}</span>
            {item?.detail && <span className="arc-subtle">{item.detail}</span>}
            {i > 0 && laterLabel && <span className="arc-subtle">{laterLabel}</span>}
            <div className="arc-spacer" />
            {!disabled && (
              <>
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                  aria-label={`Move ${name} earlier`} disabled={i === 0}
                  onClick={() => move(i, -1)}>
                  <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
                    <Icon name="chevronDown" size={13} />
                  </span>
                </button>
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                  aria-label={`Move ${name} later`} disabled={i === value.length - 1}
                  onClick={() => move(i, 1)}>
                  <Icon name="chevronDown" size={13} />
                </button>
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                  aria-label={`Remove ${name}`}
                  onClick={() => onChange(value.filter((x) => x !== id))}>
                  <Icon name="close" size={13} />
                </button>
              </>
            )}
          </div>
        );
      })}
      {value.length === 0 && <span className="arc-subtle">{emptyText}</span>}
      {!disabled && available.length > 0 && (
        <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {available.map((item) => (
            <button key={item.id} type="button" className="arc-btn arc-btn-sm"
              onClick={() => onChange([...value, item.id])}>
              <Icon name="plus" size={12} />
              <span className={mono ? 'arc-mono' : undefined}>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
